import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Validators } from '@angular/forms';
import { FilterValues } from 'app/core/interfaces/filtervalues.interface';
import { Place } from 'app/core/interfaces/place.interface';
import { CDFlorderType, CDFrom, CDIncoterm, CDShippingCondition, CDTo } from 'app/core/store/contract-details/contract-details.interface';
import { ContractInfo } from 'app/core/store/contract-info/contract-info.interface';
import { OrderDetail } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { getFirst$$, util } from 'app/core/util/util';
import { FlorderEditDeliveryComponent } from 'app/modules/florder/florder-edit-delivery/florder-edit-delivery.component';
import * as _ from 'underscore';
import { appSettings } from '../../../appSettings';

@Component({
  selector: 'app-order-edit-delivery',
  templateUrl: './order-edit-delivery.component.html',
  styleUrls: ['../../florder/florder-edit-delivery/florder-edit-delivery.component.scss'],
  animations: [
    trigger('growInOut', [
      transition(':enter', [style({ height: 0, opacity: 0 }), animate('400ms', style({ height: '50px', opacity: 1 }))]),
      transition(':leave', [animate(400, style({ height: 0, opacity: 0 }))]),
    ]),
  ],
})
export class OrderEditDeliveryComponent extends FlorderEditDeliveryComponent implements OnInit, OnChanges {
  @Input() orderDetail: OrderDetail;
  @Input() contractInfo: ContractInfo;
  @Input() filterValues: FilterValues;
  @Input() isEpsUser: boolean;
  @Input() defaultDepot: Place;
  @Input() canSelectDepot: boolean;
  @Input() busyProcess?: string;

  @Output('resetMaterialsAndPlanning') resetMaterialsAndPlanningEventEmitter = new EventEmitter<null>();
  @Output('fromSelected') fromSelectedEventEmitter = new EventEmitter<string>();
  @Output('orderTypeSelected') orderTypeSelectedEventEmitter = new EventEmitter<string>();
  @Output() emptyContract = new EventEmitter<void>();

  askIncoterms = false;
  labelWidth = appSettings.LABEL_WIDTH;

  ngOnInit(): void {
    super.ngOnInit();
    this.initOrderTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.busy && this.deliveryForm) {
      if (this.busy) {
        this.deliveryForm.get('type').disable();
      } else {
        this.deliveryForm.get('type').enable();
      }
    }
    if (changes.busyProcess) {
      this.processBusy();
    }
    if (changes.orderDetail) {
      const hasEtmOrderNumberPrevious = changes.orderDetail.previousValue && changes.orderDetail.previousValue.etmOrderNumber;
      const hasEtmOrderNumberCurrent = changes.orderDetail.currentValue.etmOrderNumber;
      if (hasEtmOrderNumberPrevious && !hasEtmOrderNumberCurrent) {
        this.ngOnInit();
      }
    }
    if (changes.contractInfo) {
      this.initOrderTypes();
      // do a deep compare of current and previous otherwhise we have a infinite loop
      const noOrderTypes = this.panelStatus.isNew && this.contractInfo.orderTypes && this.contractInfo.orderTypes.length === 0;
      const isEqualToPrevious = _.isEqual(changes.contractInfo.currentValue, changes.contractInfo.previousValue);
      if (noOrderTypes && !isEqualToPrevious) {
        this.emptyContract.emit();
      }
    }
  }

  createForm(): void {
    super.createForm();
    this.triggerValidatePONumber();
  }

  initOrderTypes() {
    if (this.contractInfo.orderTypes?.length > 0 && this.deliveryForm) {
      if (this.deliveryForm.get('type')?.value) {
        this.setShippingConditions();
      }
    } else {
      // No default selection anymore
      this.shippingConditions = [];
      this.froms = [];
      this.tos = [];
      this.incoterms = [];
    }
    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  /**
   * When ordertype changed, clear other fields in form
   */
  orderTypeChanged() {
    if (!this.busy) {
      this.deliveryForm.controls['shippingCondition'].setValue('');
      this.deliveryForm.controls['incoterm'].setValue('');
      this.deliveryForm.controls['customerReferenceNumber'].setValue('');
      this.deliveryForm.controls['senderReferenceNumber'].setValue('');
      this.deliveryForm.controls['transporter'].setValue('');
      this.deliveryForm.controls['licensePlate'].setValue('');
      this.resetMaterialsAndPlanningEventEmitter.emit();

      this.orderTypeSelectedEventEmitter.emit(this.deliveryForm.value.type);

      this.setShippingConditions();
    }
  }

  toChanged() {
    this.setValidationOnPoReferenceNumber();
    this.setIncoterms();
  }

  incotermChanged() {
    this.setValidationOnPoReferenceNumber();
    this.validateSenderRefNr();
    this.validateReceiverRefNr();
  }

  private setShippingConditions(): void {
    this.shippingConditions = util.setSelectElements<CDShippingCondition>({
      selectedId: this.getSelectedOrderType()?.id,
      containerArray: this.contractInfo.orderTypes,
      childSelector: orderType => orderType.shippingConditions,
      childControls: this.deliveryForm.get('shippingCondition'),
      defaultAll: false,
      returnObject: true,
      cascade: this.setFroms.bind(this),
      nameForLogging: 'shippingCondition'
    });
    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  setFroms(): void {
    this.froms = util.setSelectElements<CDFrom>({
      selectedId: this.getSelectedShippingCondition()?.id,
      containerArray: this.getSelectedOrderType()?.shippingConditions,
      childSelector: shippingCondition => shippingCondition.froms,
      childControls: this.deliveryForm.get('from'),
      defaultAll: false,
      returnObject: true,
      cascade: this.setTos.bind(this),
      getTitle: from => util.generateAddressString(this.filterValues.locations, from.id, this.translate.instant('SHARED.LABELS.NO_ADDRESS')),
      sort: true,
      nameForLogging: 'froms'
    });
    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  fromSelected() {
    this.fromSelectedEventEmitter.emit(null);
    this.setTos();
  }

  setTos(): void {
    let from = this.deliveryForm.get('from').value;
    let defaultAll = false;
    if (this.getSelectedShippingCondition()?.id === 'Z1') { // transport by EPS
      const orderType = this.getSelectedOrderType();
      const shippingCondition = this.getSelectedShippingCondition();

      if (orderType.id === 'EPS-CUS') {
        if (this.isEpsUser && this.canSelectDepot) {
          // Is EPS user and can select depot
          if (this.defaultDepot && (!from || from.id.trim() === '')) {
            // No from selected yet, and there is a default depot ?
            from = shippingCondition.froms.find(f => f.id === this.defaultDepot.id);
          }
        } else {
          // Is a client user: always use 'all'
          from = shippingCondition.froms.find(from => from.id === 'all');
        }

        const fromControl = this.deliveryForm.get('from');
        if (fromControl.value?.id !== from?.id) {
          fromControl.setValue(from);
        }
      }

      if (orderType.id === 'CUS-EPS') {
        defaultAll = true;
        // to is empty, take 'all to'
        const fromId = from && from.id;
        from = shippingCondition.froms.find(from => from.id === fromId);

        if (from) {
          let to = this.deliveryForm.get('to').value;
          const toId = to && to.id;
          to = to && from.tos.find(to => to.id === toId);

          if (this.isEpsUser && this.canSelectDepot) {
            // If it's an EPS user and didn't select a to yet, then take default depot if exists
            if (!to && this.defaultDepot) {
              // No from selected yet, and there is a default depot ?
              to = from.tos.find(f => f.id === this.defaultDepot.id);
            }
          } else {
            // is a client user, always use 'all'
            to = from && from.tos.find(from => from.id === 'all');
          }
          const toControl = this.deliveryForm.get('to');
          if (toControl.value?.id !== to?.id) {
            toControl.setValue(to);
          }
          this.setIncoterms();
        }
      }
    }

    this.tos = util.setSelectElements<CDTo>({
      defaultAll,
      selectedId: from && from.id,
      containerArray:
        (this.deliveryForm.get('shippingCondition').value &&
          this.contractInfo.orderTypes
            .find(ot => ot.id === this.deliveryForm.get('type').value)
            .shippingConditions.find(sc => sc.id === this.deliveryForm.get('shippingCondition').value.id).froms) ||
        [],
      childSelector: from => from.tos,
      returnObject: true,
      childControls: [this.deliveryForm.get('to')],
      sort: true,
      cascade: this.setIncoterms.bind(this),
      getTitle: to => util.generateAddressString(this.filterValues.locations, to.id, this.translate.instant('SHARED.LABELS.NO_ADDRESS')),
      nameForLogging: 'tos'
    });
    this.setValidationOnPoReferenceNumber();
    this.setIncoterms();
    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  setIncoterms(): void {
    const shippingConditions = this.contractInfo.orderTypes?.find(ot => ot?.id === this.deliveryForm.get('type').value)?.shippingConditions || [];
    const froms = (this.deliveryForm.get('shippingCondition').value && shippingConditions.find(sc => sc.id === this.deliveryForm.get('shippingCondition').value.id).froms) || [];
    let from = this.deliveryForm.get('from').value;
    from = from && froms.find(f => f.id === from.id); // Replace with full blown object, check if really in contract
    const tos = (from && from.tos) || [];
    this.incoterms = util.setSelectElements<CDTo>({
      selectedId: this.deliveryForm.get('to').value && this.deliveryForm.get('to').value.id,
      containerArray: tos,
      childSelector: to => to.incoterms,
      returnObject: true,
      childControls: [this.deliveryForm.get('incoterm')],
      defaultAll: false,
      nameForLogging: 'incoterms'
    });
    this.askIncoterms = !!this.incoterms.find(i => i.id !== '-') || (this.orderDetail.deliveryMethod.incoterm && this.orderDetail.deliveryMethod.incoterm.id !== '-');
    if (!this.askIncoterms && this.deliveryForm.get('type').value === 'CUS-EPS') {
      this.deliveryForm.get('incoterm').setValue(this.incoterms.length && this.incoterms[0]);
    }
    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  private getSelectedOrderType(): CDFlorderType {
    return this.contractInfo.orderTypes.find(orderType => orderType?.id === this.deliveryForm.get('type').value);
  }

  private getSelectedShippingCondition(): CDShippingCondition {
    return this.getSelectedOrderType()?.shippingConditions.find(shippingCondition => shippingCondition.id === this.deliveryForm.get('shippingCondition').value?.id);
  }

  private getFrom(): CDFrom {
    return this.getSelectedShippingCondition()?.froms.find(from => from.id === this.deliveryForm.get('from').value?.id);
  }

  protected getTo(): CDTo {
    return this.getFrom()?.tos.find(to => to.id === this.deliveryForm.get('to').value?.id);
  }

  private getIncoterm(): CDIncoterm {
    return this.getTo()?.incoterms.find(it => it.id === this.deliveryForm.get('incoterm').value?.id);
  }

  protected getControlsConfig() {
    return Object.assign(super.getControlsConfig(), {
      soldTo: [this.deliveryMethod.soldTo],
      shippingCondition: [this.deliveryMethod.shippingCondition, [Validators.required]],
      incoterm: [this.deliveryMethod.incoterm, [Validators.required]],
    });
  }

  doNextClicked() {
    if (!this.bothRefNrsValidated) {
      this.validateBothRefNrs().then(async (noErrors) => {
        if (noErrors) {
          const pending = await getFirst$$(this.refNrValidationPending);
          if (!pending && this.deliveryForm.valid) {
            this.nextClicked.emit(this.deliveryForm.value);
          }
        }
      });
    }
  }

  protected getEtmOrderNumber(): string {
    return this.orderDetail && this.orderDetail.etmOrderNumber;
  }

  showFrom() {
    const epsUserFilledInFrom = this.isEpsUser && !this.canSelectDepot && this.deliveryForm && this.deliveryForm.value.from && this.deliveryForm.value.from.id !== 'all';
    return (
      epsUserFilledInFrom ||
      !this.deliveryForm ||
      !this.deliveryForm.value.shippingCondition ||
      this.panelStatus.isView ||
      this.deliveryForm.value.type !== 'EPS-CUS' ||
      this.deliveryForm.value.shippingCondition.id !== 'Z1'
    );
  }

  showTo() {
    const epsUserFilledInTo = this.isEpsUser && !this.canSelectDepot && this.deliveryForm && this.deliveryForm.value.to && this.deliveryForm.value.to.id !== 'all';
    return (
      epsUserFilledInTo ||
      !this.deliveryForm ||
      !this.deliveryForm.value.shippingCondition ||
      this.panelStatus.isView ||
      this.deliveryForm.value.type !== 'CUS-EPS' ||
      this.deliveryForm.value.shippingCondition.id !== 'Z1'
    );
  }

  canEditFromField(): boolean {
    return (
      (this.isEpsUser && this.canSelectDepot) ||
      this.deliveryForm.value.type !== 'EPS-CUS' ||
      !this.deliveryForm.value.shippingCondition ||
      this.deliveryForm.value.shippingCondition.id !== 'Z1'
    );
  }

  canEditToField(): boolean {
    return (
      (this.isEpsUser && this.canSelectDepot) ||
      this.deliveryForm.value.type !== 'CUS-EPS' ||
      !this.deliveryForm.value.shippingCondition ||
      this.deliveryForm.value.shippingCondition.id !== 'Z1'
    );
  }

  private processBusy() {

  }
}
